import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Scale, Loader2, Download } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CLASSES = [
  { value: 'all', label: 'Toutes les classes' },
  { value: '1', label: '1 - Capitaux' }, { value: '2', label: '2 - Immobilisations' },
  { value: '3', label: '3 - Stocks' }, { value: '4', label: '4 - Tiers' },
  { value: '5', label: '5 - Financiers' }, { value: '6', label: '6 - Charges' },
  { value: '7', label: '7 - Produits' },
];

export default function Balance() {
  const [data, setData] = useState({ lignes: [], total_debit: 0, total_credit: 0, is_equilibree: true });
  const [loading, setLoading] = useState(true);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [classe, setClasse] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;
      if (classe !== 'all') params.classe = classe;
      const res = await axios.get(`${API_URL}/comptabilite/balance`, { params });
      setData(res.data);
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const totalSoldeD = data.lignes.reduce((s, l) => s + l.solde_debiteur, 0);
  const totalSoldeC = data.lignes.reduce((s, l) => s + l.solde_crediteur, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            <Scale className="w-6 h-6 inline mr-2" />Balance générale
          </h1>
          <p className="text-slate-500">
            {data.lignes.length} compte(s)
            {data.is_equilibree
              ? <Badge className="ml-2 bg-green-100 text-green-700">Équilibrée</Badge>
              : <Badge className="ml-2 bg-red-100 text-red-700">Déséquilibrée</Badge>
            }
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-4 gap-4">
            <div><Label>Date début</Label><Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} /></div>
            <div><Label>Date fin</Label><Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} /></div>
            <div>
              <Label>Classe</Label>
              <Select value={classe} onValueChange={setClasse}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CLASSES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end"><Button onClick={fetchData} className="bg-[#0F5156] hover:bg-[#0d464a] w-full">Filtrer</Button></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 dark:bg-slate-800">
                  <TableHead className="w-32">N° Compte</TableHead>
                  <TableHead>Libellé</TableHead>
                  <TableHead className="w-32 text-right">Débit</TableHead>
                  <TableHead className="w-32 text-right">Crédit</TableHead>
                  <TableHead className="w-32 text-right">Solde débiteur</TableHead>
                  <TableHead className="w-32 text-right">Solde créditeur</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.lignes.map(l => (
                  <TableRow key={l.compte_numero}>
                    <TableCell className="font-mono font-medium">{l.compte_numero}</TableCell>
                    <TableCell>{l.compte_libelle}</TableCell>
                    <TableCell className="text-right font-mono">{l.debit > 0 ? formatCurrency(l.debit) : ''}</TableCell>
                    <TableCell className="text-right font-mono">{l.credit > 0 ? formatCurrency(l.credit) : ''}</TableCell>
                    <TableCell className="text-right font-mono text-blue-600">{l.solde_debiteur > 0 ? formatCurrency(l.solde_debiteur) : ''}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{l.solde_crediteur > 0 ? formatCurrency(l.solde_crediteur) : ''}</TableCell>
                  </TableRow>
                ))}
                {data.lignes.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">Aucune donnée</TableCell></TableRow>
                )}
                {/* Totals row */}
                {data.lignes.length > 0 && (
                  <TableRow className="bg-slate-50 dark:bg-slate-800 font-bold">
                    <TableCell colSpan={2} className="text-right">TOTAUX</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(data.total_debit)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(data.total_credit)}</TableCell>
                    <TableCell className="text-right font-mono text-blue-600">{formatCurrency(totalSoldeD)}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{formatCurrency(totalSoldeC)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
